import { Alert } from 'lib/components/Alert';
import { Badge } from 'lib/components/Badge';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { OrderDetailModel } from 'lib/model/objects/orderDetailModel';
import { OrderModel } from 'lib/model/objects/orderModel';
import { ColumnService } from 'lib/services/directory';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { AdDetailsCardRow } from './AdDetailsCardRow';

type AdDetailsCouponsCardProps = {
  orderDetail: OrderDetailModel;
  order: OrderModel;
};

export function AdDetailsCouponsCard({
  orderDetail,
  order
}: AdDetailsCouponsCardProps) {
  const {
    value: appliedCoupons,
    isLoading,
    isError
  } = useAsyncEffect({
    fetchData: async () => {
      const [error, result] = await orderDetail.fetchAppliedCoupons();

      if (error) {
        throw error;
      }

      return result;
    },
    dependencies: [orderDetail.id],
    initialData: null,
    errorConfig: {
      service: ColumnService.ORDER_MANAGEMENT,
      message: 'Error fetching applied coupons',
      tags: { orderId: order.id, orderDetailId: orderDetail.id }
    }
  });

  if (isLoading) {
    return null;
  }

  if (!isError && (!appliedCoupons || appliedCoupons.length === 0)) {
    return null;
  }

  return (
    <AdDetailsCardRow
      label="Applied Coupons"
      value={
        <>
          {isError && (
            <Alert
              id="coupon-error"
              status="error"
              icon={<ExclamationCircleIcon className="w-4" />}
              title="Error loading coupons"
            />
          )}
          {!isError && (
            <div className="flex flex-wrap gap-1 mt-1">
              {appliedCoupons?.map(coupon => (
                <Badge status="success" key={coupon.id}>
                  {coupon.modelData.code}
                </Badge>
              ))}
            </div>
          )}
        </>
      }
    />
  );
}
